"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { RiAttachmentLine, RiCloseLine, RiFileLine } from "react-icons/ri";
const COMPOSER_FILE_ACCEPT = "image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt";

/** Pending attachments for a composer, fed by the picker, drag-and-drop and paste. */
export function useComposerFiles() {
  const $ = _c(11);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [selectedFiles, setSelectedFiles] = useState(t0);
  const [isDragOver, setIsDragOver] = useState(false);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = files => {
      if (files.length > 0) {
        setSelectedFiles(prev => [...prev, ...files]);
      }
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const addFiles = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = index => {
      setSelectedFiles(prev_0 => prev_0.filter((_, i) => i !== index));
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const removeFile = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = e => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(true);
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const handleDragOver = t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = e_0 => {
      e_0.preventDefault();
      e_0.stopPropagation();
      setIsDragOver(false);
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const handleDragLeave = t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = e_1 => {
      e_1.preventDefault();
      e_1.stopPropagation();
      setIsDragOver(false);
      addFiles(Array.from(e_1.dataTransfer.files));
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const handleDrop = t5;
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = e_2 => {
      const items = Array.from(e_2.clipboardData.items);
      const fileItems = items.filter(_temp);
      if (fileItems.length === 0) {
        return;
      }
      e_2.preventDefault();
      const files_0 = fileItems.map(_temp2).filter(_temp3);
      addFiles(files_0);
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const handlePaste = t6;
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      onDragOver: handleDragOver,
      onDragLeave: handleDragLeave,
      onDrop: handleDrop,
      onPaste: handlePaste
    };
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== isDragOver || $[9] !== selectedFiles) {
    t8 = {
      selectedFiles,
      setSelectedFiles,
      addFiles,
      removeFile,
      isDragOver,
      dropZoneProps: t7
    };
    $[8] = isDragOver;
    $[9] = selectedFiles;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  return t8;
}
function _temp3(f) {
  return f !== null;
}
function _temp2(item_0) {
  return item_0.getAsFile();
}
function _temp(item) {
  return item.kind === "file";
}
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Rows for files waiting to be sent, with image thumbnails and a remove button. */
export function ComposerFilePreviews(t0) {
  const $ = _c(13);
  const {
    files,
    onRemove
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [previewUrls, setPreviewUrls] = useState(t1);
  let t2;
  let t3;
  if ($[1] !== files) {
    t2 = () => {
      const urls = files.map(_temp4);
      setPreviewUrls(urls);
      return () => {
        urls.forEach(_temp5);
      };
    };
    t3 = [files];
    $[1] = files;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  if (files.length === 0) {
    return null;
  }
  let t4;
  if ($[4] !== files || $[5] !== onRemove || $[6] !== previewUrls) {
    let t5;
    if ($[8] !== onRemove || $[9] !== previewUrls) {
      t5 = (file_0, index) => {
        const preview = previewUrls[index];
        return <div key={`${file_0.name}-${index}`} className="flex items-center gap-2 rounded-lg border border-border bg-background-secondary px-2.5 py-1.5">{preview ? <img src={preview} alt={file_0.name} className="h-8 w-8 shrink-0 rounded object-cover" /> : <RiFileLine className="h-5 w-5 shrink-0 text-foreground-secondary" />}<div className="min-w-0 flex-1"><p className="truncate text-caption-strong text-foreground">{file_0.name}</p><p className="text-tiny text-foreground-muted">{formatFileSize(file_0.size)}</p></div><button type="button" onClick={() => onRemove(index)} aria-label={`Remove ${file_0.name}`} className="shrink-0 rounded p-0.5 text-foreground-muted transition-colors hover:text-foreground"><RiCloseLine className="h-4 w-4" /></button></div>;
      };
      $[8] = onRemove;
      $[9] = previewUrls;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    t4 = files.map(t5);
    $[4] = files;
    $[5] = onRemove;
    $[6] = previewUrls;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[11] !== t4) {
    t5 = <div className="mb-2 flex flex-col gap-1.5">{t4}</div>;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
}

/** Paperclip button with its hidden file input. */
function _temp5(url) {
  if (url) {
    URL.revokeObjectURL(url);
  }
}
function _temp4(file) {
  return file.type.startsWith("image/") ? URL.createObjectURL(file) : null;
}
export function ComposerAttachButton(t0) {
  const $ = _c(4);
  const {
    onFiles
  } = t0;
  const inputRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => inputRef.current?.click();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <button type="button" onClick={t1} aria-label="Attach files" className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-foreground-secondary transition-colors hover:bg-hover hover:text-foreground"><RiAttachmentLine className="h-4 w-4" /></button>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== onFiles) {
    t3 = <>{t2}<input ref={inputRef} type="file" multiple={true} accept={COMPOSER_FILE_ACCEPT} onChange={e => {
        onFiles(Array.from(e.target.files ?? []));
        e.target.value = "";
      }} className="hidden" /></>;
    $[2] = onFiles;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}